/**
 * Activity Tracking Context
 * Provides activity tracking functionality throughout the application
 */

import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { hasAnalyticsConsent } from '../services/cookieService';
import {
  initSession,
  logPageView,
  logFeatureUsage,
  logDataOperation,
  logError,
  logAuthEvent,
  logNavigationEvent,
  logInteractionEvent,
  logSystemEvent,
  logPerformanceMetric,
  flushOfflineActivityLog,
  endSession
} from '../services/activityTracking';

// Create context
const ActivityTrackingContext = createContext(null);

/**
 * Custom hook to use the Activity Tracking context
 * @returns {Object} Activity tracking functions
 */
export const useActivityTracking = () => {
  return useContext(ActivityTrackingContext);
};

/**
 * Activity Tracking Provider component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {React.ReactElement} Provider component
 */
export const ActivityTrackingProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const previousPathRef = useRef(null);

  // Initialize session when user logs in
  useEffect(() => {
    if (currentUser) {
      initSession(currentUser.uid);
    }
  }, [currentUser]);
  
  // Track page views (only if analytics consent is granted)
  useEffect(() => {
    if (!location) {
      return;
    }

    // Check analytics consent before tracking
    if (!hasAnalyticsConsent()) {
      return;
    }

    const currentPath = location.pathname;

    if (currentUser) {
      logPageView(currentUser.uid, currentPath, {
        metadata: {
          search: location.search,
          hash: location.hash,
        },
      });

      const previousPath = previousPathRef.current;
      if (previousPath && previousPath !== currentPath) {
        logNavigationEvent(currentUser.uid, previousPath, currentPath, {
          search: location.search,
          hash: location.hash,
        });
      }
    }

    previousPathRef.current = currentPath;
  }, [currentUser, location]);

  // End session on unmount
  useEffect(() => {
    return () => {
      if (currentUser) {
        endSession(currentUser.uid);
      }
    };
  }, [currentUser]);

  const value = useMemo(() => {
    if (!currentUser) {
      return {
        trackFeatureUsage: () => {},
        trackDataOperation: () => {},
        trackError: () => {},
        trackAuthEvent: () => {},
        trackNavigation: () => {},
        trackInteraction: () => {},
        trackSystemEvent: () => {},
        trackPerformanceMetric: () => {},
        flushOfflineQueue: async () => ({ flushed: 0, pending: 0, errors: [] }),
      };
    }

    return {
      trackFeatureUsage: (component, action, itemId, additionalData = {}, options = {}) =>
        logFeatureUsage(currentUser.uid, component, action, itemId, additionalData, options),
      trackDataOperation: (operation, collection, documentId, status, metadata = {}, options = {}) =>
        logDataOperation(currentUser.uid, operation, collection, documentId, status, metadata, options),
      trackError: (errorType, errorMessage, component, additionalData = {}, options = {}) =>
        logError(currentUser.uid, errorType, errorMessage, component, additionalData, options),
      trackAuthEvent: (eventType, method, status, additionalData = {}, options = {}) =>
        logAuthEvent(currentUser.uid, eventType, method, status, additionalData, options),
      trackNavigation: (from, to, additionalData = {}, options = {}) =>
        logNavigationEvent(currentUser.uid, from, to, additionalData, options),
      trackInteraction: (interaction, options = {}) =>
        logInteractionEvent(currentUser.uid, interaction, options),
      trackSystemEvent: (event, severity, metadata = {}, options = {}) =>
        logSystemEvent(currentUser.uid, event, severity, metadata, options),
      trackPerformanceMetric: (metric, value, metadata = {}, options = {}) =>
        logPerformanceMetric(currentUser.uid, metric, value, metadata, options),
      flushOfflineQueue: () => flushOfflineActivityLog(currentUser.uid),
    };
  }, [currentUser]);

  return (
    <ActivityTrackingContext.Provider value={value}>
      {children}
    </ActivityTrackingContext.Provider>
  );
}; 